import React, { useContext, type FC } from 'react';

import { Heading, Box, Text, SideModal, ImageWithPlaceholder, LayoutContext, reactHTMLParser } from '@lucid/components';
import { useSmartlingStrings, withImage } from '@lucid/hooks';

import type { PersonGeneric } from './person';

export interface BioModalProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The person whose bio is being shown */
  person: PersonGeneric;
  /** Callback passed to close the modal */
  handleCloseModal: () => void;
  isModalOpen?: boolean;
}

const BioDetail = ({ title, value, shouldParse }) => (
  <Box
    css={theme => ({
      paddingTop: theme.space.s16,
      borderTop: `1px solid ${theme.colors.pink[40]}`,
    })}
  >
    <Heading as="h4" css={theme => ({ marginBottom: theme.space.s8, color: theme.colors.white })} variant="headingS">
      {title}
    </Heading>
    {shouldParse ? (
      <Text
        as={value?.includes('<ul>') ? 'div' : 'p'}
        css={theme => [
          theme.shared.paragraphNestedLinkStyles(theme, 'secondaryInverse'),
          {
            a: { display: 'inline-flex', '&:hover': { fontWeight: theme.fontWeights.fw400 } },
            ul: { listStyle: 'circle' },
            li: { marginBottom: theme.space.s8 },
          },
        ]}
      >
        {reactHTMLParser(value)}
      </Text>
    ) : (
      value
    )}
  </Box>
);

const BioModal: FC<BioModalProps> = ({ handleCloseModal, person, isModalOpen }) => {
  const { shortCode } = useContext(LayoutContext);

  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_leadership_bio_year',
      'mktg_leadership_education',
      'mktg_leadership_fav_video',
      'mktg_leadership_show_more',
      'mktg_leadership_show_less',
      'mktg_lately_at_lucid_sessions',
    ],
    shortCode,
  });
  type BioDetailType = {
    i18nKey: string;
    value: string;
    shouldParse?: boolean;
  };
  const bioDetails = [
    person?.bioDetails,
    { i18nKey: 'mktg_leadership_bio_year', value: person?.start_year },
    { i18nKey: 'mktg_leadership_education', value: person?.education },
    { i18nKey: 'mktg_leadership_fav_video', value: person?.favorite_video },
  ].filter(bd => !!bd?.value) as BioDetailType[];
  const hasAdditionalData = !!bioDetails.length;

  return (
    <SideModal handleCloseModal={handleCloseModal} isModalOpen={isModalOpen}>
      <Box as="figure" css={theme => ({ margin: 0, padding: theme.space.s32, maxWidth: '600px' })}>
        <ImageWithPlaceholder
          isBackgroundImage
          alt={`${person.name} headshot`}
          aspectRatio="1:1"
          css={theme => ({
            maxWidth: '300px',
            marginBottom: theme.space.s24,
            div: {
              backgroundSize: '101%',
              backgroundPosition: 'top',
            },
          })}
          src={withImage(person.image)}
        />
        <figcaption>
          <Heading
            as="h3"
            css={theme => ({ color: theme.colors.white, marginBottom: theme.space.s4 })}
            variant="headingM"
          >
            {person.name}
          </Heading>
          <Text css={theme => ({ color: theme.colors.coolGrey[30] })} variant="paragraphS400">
            {person.title}
          </Text>
        </figcaption>
        {person.bio && (
          <Text
            css={theme => ({
              marginBottom: theme.space.s8,
              marginTop: theme.space.s40,
              color: theme.colors.white,
            })}
            variant="paragraphS400"
          >
            {person.bio}
          </Text>
        )}
      </Box>
      {hasAdditionalData && (
        <Box
          css={theme => ({
            backgroundColor: theme.colors.coolGrey[100],
            color: 'white',
          })}
        >
          <Box
            css={theme => ({
              display: 'grid',
              flexDirection: 'column',
              gridTemplateColumns: '1fr',
              padding: theme.space.s24,
              width: 'fit-content',
              minWidth: '225px',
              height: '100%',
              gap: theme.space.s16,
              justifyContent: 'space-between',
              [theme.breakpoints.bp500.min]: {
                gridTemplateColumns: '1fr 1fr',
                width: '100%',
              },
              [theme.breakpoints.bp900.min]: {
                gridTemplateColumns: '1fr',
                width: 'fit-content',
              },
            })}
          >
            {bioDetails.map(({ i18nKey, value, shouldParse }) => (
              <BioDetail
                key={withSmartling(i18nKey)}
                shouldParse={shouldParse}
                title={withSmartling(i18nKey)}
                value={value}
              />
            ))}
          </Box>
        </Box>
      )}
    </SideModal>
  );
};

export { BioModal };
export default BioModal;
